import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { HttpProblemError } from '@collector/shared'
import { logError } from '../lib/log'

/**
 * Route-level error element. Rendered by React Router whenever a loader,
 * action, or render in the subtree throws. The goal is a calm, informative
 * screen that (a) does not leak stack traces or arbitrary error messages to
 * the operator, (b) still gives a developer enough to work with in the
 * console, and (c) offers two clear recovery actions.
 */
export function ErrorFallback() {
  const { t } = useTranslation()
  const error = useRouteError()

  useEffect(() => {
    logError('route.error', error, {
      kind: classify(error),
    })
  }, [error])

  const kind = classify(error)
  const title = titleFor(kind, t)
  const description = descriptionFor(kind, t)
  const technical = technicalFor(error)

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-900">
        <h1 className="text-lg font-semibold text-stone-900 dark:text-stone-100">{title}</h1>
        <p className="mt-2 text-sm text-stone-600 dark:text-stone-400">{description}</p>
        {technical ? (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs font-medium text-stone-500 dark:text-stone-400">
              {t('errors.technicalDetails')}
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded bg-stone-50 p-3 text-[0.7rem] leading-snug text-stone-700 dark:bg-stone-950 dark:text-stone-200">
              {technical}
            </pre>
          </details>
        ) : null}
        <div className="mt-5 flex gap-2">
          <button
            className="btn-secondary"
            onClick={() => {
              window.location.reload()
            }}
          >
            {t('common.reload')}
          </button>
          <Link to="/" className="btn-primary">
            {t('common.backToDashboard')}
          </Link>
        </div>
      </div>
    </div>
  )
}

type ErrorKind = 'not_found' | 'forbidden' | 'unauthorized' | 'network' | 'route' | 'unknown'

type TFn = ReturnType<typeof useTranslation>['t']

function classify(error: unknown): ErrorKind {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) return 'not_found'
    if (error.status === 403) return 'forbidden'
    if (error.status === 401) return 'unauthorized'
    return 'route'
  }
  if (error instanceof HttpProblemError) {
    if (error.status === 0) return 'network'
    if (error.status === 404) return 'not_found'
    if (error.status === 403) return 'forbidden'
    if (error.status === 401) return 'unauthorized'
  }
  return 'unknown'
}

function titleFor(kind: ErrorKind, t: TFn): string {
  switch (kind) {
    case 'not_found':
      return t('errors.pageNotFound')
    case 'forbidden':
      return t('errors.forbidden')
    case 'unauthorized':
      return t('errors.sessionExpired')
    case 'network':
      return t('errors.network')
    default:
      return t('errors.generic')
  }
}

function descriptionFor(kind: ErrorKind, t: TFn): string {
  switch (kind) {
    case 'not_found':
      return t('errors.pageNotFoundBody')
    case 'forbidden':
      return t('errors.forbiddenBody')
    case 'unauthorized':
      return t('errors.sessionExpiredBody')
    case 'network':
      return t('errors.networkBody')
    default:
      return t('errors.genericBody')
  }
}

function technicalFor(error: unknown): string | null {
  if (isRouteErrorResponse(error)) {
    const extra = typeof error.data === 'string' ? `\n${error.data}` : ''
    return `${error.status} ${error.statusText}${extra}`
  }
  if (error instanceof HttpProblemError) {
    const p = error.problem
    const parts = [
      `HTTP ${p.status} ${p.title}`,
      p.code ? `code: ${p.code}` : null,
      p.detail ?? null,
      p.requestId ? `request-id: ${p.requestId}` : null,
    ].filter(Boolean)
    return parts.join('\n')
  }
  if (error instanceof Error) {
    // Include the name + message, but not the stack — that lives in the
    // console via logError above, not on the visible UI.
    return `${error.name}: ${error.message}`
  }
  return null
}
